import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Skeleton } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { useTranslation } from '@root/shared/localization'

import type { TransferFlowReatom } from '../../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  model: TransferFlowReatom
} & ClassNameProps

export const TransferTotals: React.FC<Props> = ({ className, model }) => {
  const { t } = useTranslation()

  const [currency] = useAtom(model.currency.currencyAtom)
  const [totalAmount] = useAtom(model.totalTransferAmountAtom)
  const [totalAmountUsd] = useAtom(model.totalTransferAmountUsdAtom)
  const [maxTotalAmount] = useAtom(model.maxTotalTransferAmountAtom)

  return (
    <Root className={className}>
      <Start>
        <TotalLabel>{t('common.total')}:</TotalLabel>
        {CurrencyModel.isEthOrWeth(currency) ? (
          <Label>{t('transfer.amount-plus-gas-fee')}</Label>
        ) : undefined}
      </Start>
      <Points />
      <End>
        <AmountContainer>
          <AmountUsd>
            {totalAmountUsd ? (
              <Numeric
                value={totalAmountUsd}
                preset="currency-amount-short"
                currency="$"
              />
            ) : (
              <Skeleton height={17} paddingBlock={2} width={30} />
            )}
          </AmountUsd>
          <Amount>
            {totalAmount ? (
              <Numeric
                value={totalAmount}
                preset="currency-amount-short"
                currency={currency.ticker}
              />
            ) : (
              <Skeleton height={17} paddingBlock={2} width={60} />
            )}
          </Amount>
        </AmountContainer>
        {CurrencyModel.isEthOrWeth(currency) ? (
          <MaxAmount>
            <span>{t('transfer.max-amount')}: </span>
            {maxTotalAmount ? (
              <Numeric
                value={maxTotalAmount}
                preset="currency-amount-short"
                currency={currency.ticker}
              />
            ) : (
              <Skeleton height={16} paddingBlock={3} width={60} />
            )}
          </MaxAmount>
        ) : undefined}
      </End>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  font-size: 11px;
  font-weight: 500;
  align-items: end;
  justify-content: end;
`

const Start = styled.div`
  display: grid;
`

const Points = styled.div`
  height: 4px;
  align-self: flex-end;
  background-image: repeating-radial-gradient(
    circle,
    var(--background-10) 0 1px,
    transparent 1px 4px
  );
  background-size: 5px 5px;
  margin: 0 4px 1px;
`
const End = styled.div`
  display: grid;
  justify-items: end;
`

const Label = styled.div`
  color: var(--text-10);
`

const AmountContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-items: end;
  gap: 8px;
`
const Amount = styled.div`
  font-weight: 700;
`

const AmountUsd = styled.div`
  color: var(--text-10);
`

const TotalLabel = styled.div``

const MaxAmount = styled.div`
  color: var(--text-10);

  font-weight: 700;

  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 4px;
`
